import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';

const PrivacyPolicyPage: React.FC = () => {
  const [contactOpen, setContactOpen] = React.useState(false);
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      <Box sx={{ pt: 15, pb: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Legal" color="secondary" sx={{ mb: 2 }} />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Privacy Policy for CleoLead Users
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Last updated: February 17, 2026
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This policy applies to businesses and individuals who sign up for and use the CleoLead platform.
            </Typography>
            <Button
              component={RouterLink}
              to="/privacy/end-user"
              variant="text"
              size="small"
              sx={{ mt: 1, color: '#C75B39' }}
            >
              Looking for the End-User Privacy Policy?
            </Button>
          </Box>

          <Card sx={{ p: 4, mb: 4 }}>
            <Typography variant="body1" paragraph>
              At CleoLead Corp. ("CleoLead", "we", "our", or "us"), we are committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you sign up for and use our AI-assisted customer engagement platform as a business user.
            </Typography>
            <Typography variant="body1">
              For information about how we handle data of your customers (end-users who interact
              with your AI assistant), please see our{' '}
              <RouterLink to="/privacy/end-user" style={{ color: '#C75B39' }}>
                End-User Privacy Policy
              </RouterLink>.
            </Typography>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            1. Information We Collect
          </Typography>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            1.1 Information You Provide
          </Typography>
          <List>
            {[
              'Account information (name, email, password)',
              'Business information (company name, industry, website)',
              'Payment information (processed securely via Stripe)',
              'Content you upload (documents, training data)',
              'Communications with our support team',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            1.2 Information Collected Automatically
          </Typography>
          <List>
            {[
              'Usage data (features used, pages visited)',
              'Device information (browser type, operating system)',
              'IP address and general location',
              'Cookies and similar tracking technologies',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            1.3 Conversation Data
          </Typography>
          <Typography variant="body1" paragraph>
            When you connect your messaging platforms, we process conversations between your AI assistant
            and your customers. This data is used to:
          </Typography>
          <List>
            {[
              'Power AI responses',
              'Train and improve your AI assistant',
              'Generate analytics and insights',
              'Capture and export lead information',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the information we collect to:
          </Typography>
          <List>
            {[
              'Provide, maintain, and improve our services',
              'Process transactions and send related information',
              'Send technical notices, updates, and support messages',
              'Respond to your comments, questions, and customer service requests',
              'Monitor and analyze trends, usage, and activities',
              'Detect, investigate, and prevent fraudulent transactions and abuse',
              'Personalize and improve your experience',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            2a. AI Profiling
          </Typography>
          <Typography variant="body1" paragraph>
            Our AI may analyze conversation content to better serve you. This analysis may include:
          </Typography>
          <List>
            {[
              'Identifying topics of interest and preferences',
              'Assessing communication style for personalized responses',
              'Extracting relevant facts mentioned in conversations',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" paragraph>
            This profiling helps businesses provide more relevant and personalized service.
            You may request deletion of your profile data at any time.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            3. Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell your personal information. We may share your information in the following situations:
          </Typography>
          <List>
            {[
              'With your consent',
              'With service providers who assist in our operations',
              'To comply with legal obligations',
              'To protect our rights, privacy, safety, or property',
              'In connection with a merger, acquisition, or sale of assets',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            3a. Third-Party AI Service Providers
          </Typography>
          <Typography variant="body1" paragraph>
            We use third-party AI service providers to process conversation data and generate
            responses. These providers include:
          </Typography>
          <List>
            {[
              'Anthropic (Claude) — primary LLM for generating conversation responses and analyzing conversations',
              'OpenAI — image/vision analysis and knowledge-base embeddings',
              'Google (Gemini) — AI image generation for assistant avatars',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" paragraph>
            These providers process data according to their own privacy policies and data
            processing agreements. Conversation data sent to these providers is used solely
            for generating responses and is not used to train their models.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            4. Data Storage and Security
          </Typography>
          <Typography variant="body1" paragraph>
            Your data is stored securely and is only accessible to you. We implement strict data isolation
            ensuring that each user's data is completely separated and protected from unauthorized access.
            Our security measures include:
          </Typography>
          <List>
            {[
              'All data is encrypted at rest using AES-256 encryption',
              'Data in transit is protected with TLS 1.3 encryption',
              'User data is isolated and only accessible by the authenticated account owner',
              'Role-based access controls ensure data is only accessed by authorized users',
              'Regular security assessments and penetration testing',
              'Multi-factor authentication support for enhanced account security',
              'Automated backups with encryption',
              'Incident response procedures and 24/7 monitoring',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            5. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your information for as long as your account is active or as needed to provide services.
            Specific retention periods are as follows:
          </Typography>
          <List>
            {[
              'Conversation data: Retained while your business account is active, deleted within 90 days of account closure',
              'Client profiles: Retained while the associated business account is active',
              'Payment data: Retained as required by tax and financial regulations (typically 7 years)',
              'Analytics data: Aggregated and anonymized after 12 months',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" paragraph>
            You can request deletion of your data at any time by contacting us.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            6. Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have certain rights regarding your personal information:
          </Typography>
          <List>
            {[
              'Access your personal data',
              'Correct inaccurate data',
              'Request deletion of your data',
              'Object to processing',
              'Data portability',
              'Withdraw consent',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            7. Data Deletion Request
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to request deletion of your personal data at any time. When you request data deletion,
            we will permanently remove all your personal information from our systems, including:
          </Typography>
          <List>
            {[
              'Account information and profile data',
              'Conversation history and chat data',
              'Uploaded documents and training data',
              'Analytics and usage data',
              'Any data obtained from connected platforms (Facebook, Instagram, Telegram)',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            How to Request Data Deletion
          </Typography>
          <Typography variant="body1" paragraph>
            To request deletion of your data:
          </Typography>
          <List>
            {[
              'Contact support@cleolead.com with your request',
              'Specify the platform (Facebook, Instagram, Telegram) and your username or ID',
              'We will process deletion within 30 days',
              'For Facebook/Instagram users: Meta may also send deletion requests on your behalf, which we process automatically via our data deletion callback',
              'You will receive confirmation once your data has been deleted',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Card sx={{ p: 3, bgcolor: 'rgba(59, 130, 246, 0.1)', mt: 3 }}>
            <Typography variant="body1">
              <strong>Note:</strong> If you signed up using Facebook Login or connected your Facebook/Instagram account,
              you can also request data deletion directly through Facebook's settings. We will receive and process
              deletion requests submitted through Facebook's platform.
            </Typography>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            8. GDPR Compliance (European Economic Area)
          </Typography>
          <Typography variant="body1" paragraph>
            For users in the European Economic Area (EEA):
          </Typography>
          <List>
            {[
              'Data Controller: CleoLead Corp.',
              'Data Protection Contact: support@cleolead.com',
              'Lawful bases for processing: Consent (messaging), Contract (service provision), Legitimate Interests (service improvement)',
              'Cross-border transfers: Data may be transferred to the US and Canada. We use standard contractual clauses to protect your data.',
              'Right to complain: You may lodge a complaint with your local data protection authority',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            9. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </Typography>
          <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body1">
              <strong>Email:</strong> <span onClick={() => setContactOpen(true)} style={{ color: '#C75B39', cursor: 'pointer', textDecoration: 'underline' }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setContactOpen(true); }}>support@cleolead.com</span>
            </Typography>
          </Card>
        </Container>
      </Box>

      <Footer />
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Privacy Inquiry"
        subtitle="Have questions about our Privacy Policy? Send us a message."
        formType="privacy"
        defaultSubject="Privacy Inquiry"
      />
    </Box>
  );
};

export default PrivacyPolicyPage;
